import { createSlice, createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';

import { queryInboxTransactions, getConversationActivity, receiptKey } from '../util/inboxActivity';
import {
  markSharedConversationRead,
  readSharedNotifications,
  writeSharedNotifications,
} from '../util/notificationCache';

import { authInfo } from './auth.duck';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)

// Each refresh reads every conversation of the user (Marketplace API credits grow with the
// inbox size), so automatic refreshes (timer, focus, navigation) share this minimum age.
export const NOTIFICATION_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const countUnread = conversationActivity => {
  const unread = Object.values(conversationActivity).filter(a => a.unread);
  return {
    saleNotificationsCount: unread.filter(a => a.tab === 'sales').length,
    orderNotificationsCount: unread.filter(a => a.tab === 'orders').length,
  };
};

const fetchCurrentUserNotificationsPayloadCreator = async (
  _,
  { extra: sdk, getState, rejectWithValue }
) => {
  const startedAt = Date.now();
  // On the first page load the store has no currentUser yet: the response identifies the user.
  let userId = getState().user.currentUser?.id?.uuid;
  try {
    const userResponse = await sdk.currentUser.show();
    const user = denormalisedResponseEntities(userResponse)[0];
    userId = user.id.uuid;
    const transactions = await queryInboxTransactions(sdk);
    const conversationActivity = {};
    // Sequential requests avoid a burst of one request per conversation.
    for (const tx of transactions) {
      conversationActivity[tx.id.uuid] = await getConversationActivity(sdk, tx, user);
    }
    return {
      conversationActivity,
      userId: user.id.uuid,
      fetchedAt: startedAt,
      ...countUnread(conversationActivity),
    };
  } catch (e) {
    // A failure may come after most of the inbox was read: it waits like a success.
    return rejectWithValue(storableError(e), {
      fetchedAt: startedAt,
      userId,
    });
  }
};

// Each conversation has its own private top-level field: updates do not replace other receipts.
export const markConversationRead = (id, readAt) => async (dispatch, getState, sdk) => {
  const currentUser = getState().user.currentUser;
  const key = receiptKey(id);
  const previous = Number(currentUser?.attributes?.profile?.privateData?.[key]) || 0;
  if (!currentUser || readAt <= previous) return;
  const response = await sdk.currentUser.updateProfile(
    { privateData: { [key]: readAt } },
    { expand: true }
  );
  dispatch(setCurrentUser(denormalisedResponseEntities(response)[0]));
  // The read receipt only changes this conversation: avoid re-reading the whole inbox.
  dispatch(conversationMarkedRead(id));
  markSharedConversationRead(currentUser.id.uuid, id);
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator,
  {
    condition: (options, { getState }) => {
      const { notificationFetchInProgress, notificationFetchedAt = 0 } = getState().user;
      const maxAgeMs = options?.maxAgeMs ?? NOTIFICATION_REFRESH_INTERVAL_MS;
      return !notificationFetchInProgress && Date.now() - notificationFetchedAt >= maxAgeMs;
    },
  }
);

// Backward compatible wrapper for the thunk
/**
 * Refresh unread conversation counts unless a refresh is running or the last one is recent.
 * A recent refresh made by another tab or page load of this browser is reused instead.
 * @param {Object} [options]
 * @param {number} [options.maxAgeMs] Minimum age of the previous refresh (default 10 minutes)
 * @param {string} [options.userId] Current user's uuid when the store does not have it yet
 */
export const fetchCurrentUserNotifications = options => (dispatch, getState, sdk) => {
  const maxAgeMs = options?.maxAgeMs ?? NOTIFICATION_REFRESH_INTERVAL_MS;
  const { currentUser, notificationFetchInProgress, notificationFetchedAt = 0 } = getState().user;
  const shared = readSharedNotifications(options?.userId || currentUser?.id?.uuid);
  if (
    !notificationFetchInProgress &&
    shared &&
    shared.fetchedAt > notificationFetchedAt &&
    Date.now() - shared.fetchedAt < maxAgeMs
  ) {
    dispatch(sharedNotificationsRestored(shared));
    return Promise.resolve(shared);
  }
  return dispatch(fetchCurrentUserNotificationsThunk(options)).then(action => {
    const { payload, meta } = action;
    if (fetchCurrentUserNotificationsThunk.fulfilled.match(action)) {
      writeSharedNotifications({
        userId: payload.userId,
        fetchedAt: payload.fetchedAt,
        activity: payload.conversationActivity,
      });
    } else if (meta?.rejectedWithValue && meta.fetchedAt) {
      writeSharedNotifications({ userId: meta.userId, fetchedAt: meta.fetchedAt });
    }
    return unwrapResult(action);
  });
};

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          // The store gets currentUser only after this thunk: pass the id for the shared state.
          dispatch(fetchCurrentUserNotifications({ userId: currentUser.id.uuid })).catch(() => {});
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsError: null,
    notificationFetchInProgress: false,
    notificationFetchedAt: 0,
    conversationActivity: {},
    notificationPreviewsMissing: false,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.notificationFetchInProgress = false;
      state.notificationFetchedAt = 0;
      state.conversationActivity = {};
      state.notificationPreviewsMissing = false;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;

      state.currentUserNotificationCountError = null;
    },
    // Shared state has no message previews: keep the ones this tab already has.
    sharedNotificationsRestored: (state, action) => {
      const { userId, fetchedAt, activity } = action.payload;
      if (state.currentUser && state.currentUser.id.uuid !== userId) return;
      state.notificationFetchedAt = fetchedAt;
      if (!activity) return;
      state.conversationActivity = Object.entries(activity).reduce((merged, [id, a]) => {
        const preview = state.conversationActivity[id]?.preview;
        merged[id] = preview === undefined ? a : { ...a, preview };
        return merged;
      }, {});
      state.notificationPreviewsMissing = Object.values(state.conversationActivity).some(
        a => a.preview === undefined
      );
      const counts = countUnread(state.conversationActivity);
      state.currentUserSaleNotificationCount = counts.saleNotificationsCount;
      state.currentUserOrderNotificationCount = counts.orderNotificationsCount;
    },
    conversationMarkedRead: (state, action) => {
      const activity = state.conversationActivity[action.payload];
      if (!activity?.unread) return;
      activity.unread = false;
      const counts = countUnread(state.conversationActivity);
      state.currentUserSaleNotificationCount = counts.saleNotificationsCount;
      state.currentUserOrderNotificationCount = counts.orderNotificationsCount;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.notificationFetchInProgress = true;
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.notificationFetchInProgress = false;
        if (state.currentUser?.id?.uuid !== action.payload.userId) return;
        state.notificationFetchedAt = action.payload.fetchedAt;
        state.conversationActivity = action.payload.conversationActivity;
        state.notificationPreviewsMissing = false;
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        state.notificationFetchInProgress = false;
        const { fetchedAt, userId } = action.meta || {};
        // A failure that started for a previous user must not delay the next user's refresh.
        if (fetchedAt && (!userId || state.currentUser?.id?.uuid === userId)) {
          state.notificationFetchedAt = fetchedAt;
        }
        console.error(action.payload); // eslint-disable-line
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const {
  clearCurrentUser,
  conversationMarkedRead,
  setCurrentUser,
  setCurrentUserHasOrders,
  sharedNotificationsRestored,
} = userSlice.actions;

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
